import type { HTMLAttributes, ReactNode } from 'react'

type CardDensity = 'compact' | 'regular' | 'spacious' | 'flush'

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
  hover?: boolean
  bordered?: boolean
  /**
   * Mobile-first padding scale (Phase 1 mobile overhaul, 2026-10-13).
   * - `compact`  → 14px / 20px  (`.card-body`) — for card grids where the
   *               image dominates and the body copy is short
   * - `regular`  → 20px / 24px  (`p-5 md:p-6`) — default for most cards
   * - `spacious` → 24px / 32px  (`p-6 md:p-8`) — hero / featured spotlights
   * - `flush`    → no padding   (`p-0`) — the card is a shell for blocks
   *               that bring their own padding (a bleed-to-the-edge image,
   *               a full-bleed colour band). The card's radius and border
   *               still frame the content.
   *
   * Replaces hand-set `p-5`, `p-card`, `p-6` inside consumers. Every call
   * site that previously wrote its own padding should now pass a density
   * prop instead.
   */
  density?: CardDensity
}

/**
 * Density → Tailwind class map.
 * Kept tiny on purpose — the `.card-body` rule in globals.css already
 * encodes the compact mobile/tablet values via tokens, so `compact` here
 * is just the class name.
 */
const densityClass: Record<CardDensity, string> = {
  compact: 'card-body',
  regular: 'p-5 md:p-6',
  spacious: 'p-6 md:p-8',
  flush: 'p-0',
}

/**
 * Card — base container for content blocks.
 * Padding is now driven by `density` (defaults to `regular`, which is
 * 20px mobile / 24px desktop — matches the previous `p-card` behaviour
 * on mobile, +4px on desktop).
 */
export default function Card({
  children,
  hover = false,
  bordered = true,
  density = 'regular',
  className = '',
  ...rest
}: CardProps) {
  const classes = [
    'bg-surface-2 rounded-yada-xl transition-all duration-300',
    densityClass[density],
    bordered ? 'border border-border' : '',
    hover ? 'hover:-translate-y-1 hover:shadow-yada-lg hover:border-primary' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={classes} {...rest}>
      {children}
    </div>
  )
}
